import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Type,
  Image as ImageIcon,
  Square,
  Circle,
  Download,
  Save,
  Trash2,
  Sun,
  Sparkles,
  Bold,
  Italic,
  ChevronLeft,
  Plus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Pre-built CTA Templates
const CTA_TEMPLATES = [
  { text: 'Learn More', bg: '#8B5CF6', color: '#ffffff' },
  { text: 'Subscribe Now', bg: '#EF4444', color: '#ffffff' },
  { text: 'Get Started', bg: '#10B981', color: '#ffffff' },
  { text: 'Shop Now', bg: '#FBBF24', color: '#000000' },
  { text: 'Free Trial', bg: '#3B82F6', color: '#ffffff' },
  { text: 'Limited Offer', bg: '#EC4899', color: '#ffffff' },
];

const FONTS = ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana', 'Impact', 'Comic Sans MS'];

export default function ImageEditor() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [image, setImage] = useState(null);
  const [textLayers, setTextLayers] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Text properties
  const [newText, setNewText] = useState('Your Text Here');
  const [fontSize, setFontSize] = useState(32);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [textColor, setTextColor] = useState('#ffffff');
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  
  // Filters
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  
  // Library
  const [libraryImages, setLibraryImages] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setImage(event.target.result);
      toast.success('Image loaded!');
    };
    reader.readAsDataURL(file);
  };

  // Add text layer
  const addTextLayer = () => {
    const layer = {
      id: Date.now(),
      text: newText,
      x: 50,
      y: 50,
      fontSize,
      fontFamily,
      color: textColor,
      strokeColor,
      strokeWidth,
      bold: isBold,
      italic: isItalic,
    };
    setTextLayers([...textLayers, layer]);
    setSelectedLayer(layer.id);
    toast.success('Text added!');
  };

  // Add CTA
  const addCTA = (cta) => {
    const layer = {
      id: Date.now(),
      text: cta.text,
      x: 50,
      y: 50,
      fontSize: 28,
      fontFamily: 'Arial',
      color: cta.color,
      backgroundColor: cta.bg,
      padding: 15,
      borderRadius: 8,
      bold: true,
      italic: false,
      strokeWidth: 0,
    };
    setTextLayers([...textLayers, layer]);
    setSelectedLayer(layer.id);
    toast.success('CTA added!');
  };

  // Update layer position
  const updateLayerPosition = (id, x, y) => {
    setTextLayers(layers => 
      layers.map(l => l.id === id ? { ...l, x, y } : l)
    );
  };

  // Delete selected layer
  const deleteSelectedLayer = () => {
    if (!selectedLayer) return;
    setTextLayers(layers => layers.filter(l => l.id !== selectedLayer));
    setSelectedLayer(null);
    toast.success('Layer deleted');
  };

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw image if loaded
    if (image) {
      const img = new Image();
      img.onload = () => {
        // Apply filters
        ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
        
        // Calculate fit
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;
        
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        ctx.filter = 'none';
        
        // Draw text layers
        drawTextLayers(ctx);
      };
      img.src = image;
    } else {
      drawTextLayers(ctx);
    }
  }, [image, textLayers, brightness, contrast, selectedLayer]);

  const drawTextLayers = (ctx) => {
    textLayers.forEach(layer => {
      ctx.save();
      
      // Set font
      const fontStyle = `${layer.italic ? 'italic' : ''} ${layer.bold ? 'bold' : ''} ${layer.fontSize}px ${layer.fontFamily}`;
      ctx.font = fontStyle.trim();
      
      // Draw background if CTA
      if (layer.backgroundColor) {
        const metrics = ctx.measureText(layer.text);
        const padding = layer.padding || 10;
        const bgX = layer.x - padding;
        const bgY = layer.y - layer.fontSize - padding + 5;
        const bgW = metrics.width + padding * 2;
        const bgH = layer.fontSize + padding * 2;
        
        ctx.fillStyle = layer.backgroundColor;
        if (layer.borderRadius) {
          roundRect(ctx, bgX, bgY, bgW, bgH, layer.borderRadius);
        } else {
          ctx.fillRect(bgX, bgY, bgW, bgH);
        }
      }
      
      // Draw stroke
      if (layer.strokeWidth > 0) {
        ctx.strokeStyle = layer.strokeColor || '#000000';
        ctx.lineWidth = layer.strokeWidth;
        ctx.strokeText(layer.text, layer.x, layer.y);
      }
      
      // Draw text
      ctx.fillStyle = layer.color;
      ctx.fillText(layer.text, layer.x, layer.y);
      
      // Draw selection box
      if (selectedLayer === layer.id) {
        const metrics = ctx.measureText(layer.text);
        ctx.strokeStyle = '#8B5CF6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(
          layer.x - 5,
          layer.y - layer.fontSize - 5,
          metrics.width + 10,
          layer.fontSize + 15
        );
        ctx.setLineDash([]);
      }
      
      ctx.restore();
    });
  };

  // Rounded rectangle helper
  const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
  };

  // Handle canvas click for layer selection
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Find clicked layer
    const ctx = canvas.getContext('2d');
    let found = null;
    
    for (const layer of textLayers) {
      ctx.font = `${layer.bold ? 'bold' : ''} ${layer.fontSize}px ${layer.fontFamily}`;
      const metrics = ctx.measureText(layer.text);
      
      if (
        x >= layer.x - 5 &&
        x <= layer.x + metrics.width + 5 &&
        y >= layer.y - layer.fontSize - 5 &&
        y <= layer.y + 10
      ) {
        found = layer.id;
      }
    }
    
    setSelectedLayer(found);
  };

  // Handle drag
  const handleCanvasDrag = (e) => {
    if (!selectedLayer || e.buttons !== 1) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    updateLayerPosition(selectedLayer, x, y);
  };

  // Check if canvas has content
  const hasContent = image || textLayers.length > 0;

  // Download canvas
  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('No canvas available');
      return;
    }
    if (!hasContent) {
      toast.error('Please load an image or add text first');
      return;
    }
    try {
      const link = document.createElement('a');
      link.download = `edited-image-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Image downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  // Save to library
  const saveToLibrary = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('No canvas available');
      return;
    }
    if (!hasContent) {
      toast.error('Please load an image or add text first');
      return;
    }
    setSaving(true);
    try {
      const base64 = canvas.toDataURL('image/png').split(',')[1];
      
      await axios.post(
        `${API}/api/content/image/save`,
        { image_base64: base64, prompt: 'Edited image', website: 'saketh.ai' },
        { withCredentials: true }
      );
      
      toast.success('Image saved to library!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  // Use in video
  const useInVideo = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('No canvas available');
      return;
    }
    if (!hasContent) {
      toast.error('Please load an image or add text first');
      return;
    }
    try {
      const base64 = canvas.toDataURL('image/png').split(',')[1];
      sessionStorage.setItem('editedImage', base64);
      navigate('/video?fromEditor=true');
      toast.success('Image ready for video!');
    } catch (error) {
      console.error('Use in video error:', error);
      toast.error('Failed to prepare image for video');
    }
  };

  // Load library images
  const loadLibraryImages = async () => {
    try {
      const response = await axios.get(`${API}/api/content?content_type=image&limit=20`, {
        withCredentials: true
      });
      const images = Array.isArray(response.data) ? response.data.filter(item => item.image_base64) : [];
      setLibraryImages(images);
      setShowLibrary(true);
    } catch (error) {
      console.error('Failed to load library:', error);
      toast.error('Failed to load library');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold">Image Editor</h1>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={downloadImage}
            disabled={!hasContent}
            className={!hasContent ? 'opacity-50 cursor-not-allowed' : ''}
          >
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
          <Button 
            variant="outline" 
            onClick={saveToLibrary} 
            disabled={saving || !hasContent}
            className={(!hasContent && !saving) ? 'opacity-50 cursor-not-allowed' : ''}
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button 
            onClick={useInVideo} 
            disabled={!hasContent}
            className={`bg-violet-600 hover:bg-violet-500 ${!hasContent ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Sparkles className="w-4 h-4 mr-2" /> Use in Video
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Left Panel - Tools */}
        <div className="w-80 space-y-4">
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <Tabs defaultValue="text">
              <TabsList className="w-full grid grid-cols-3 mb-4">
                <TabsTrigger value="text"><Type className="w-4 h-4" /></TabsTrigger>
                <TabsTrigger value="cta"><Sparkles className="w-4 h-4" /></TabsTrigger>
                <TabsTrigger value="filters"><Sun className="w-4 h-4" /></TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="space-y-3">
                <div>
                  <Label className="text-sm text-white font-medium">Text</Label>
                  <Input
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="Enter your text..."
                    className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500 mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-sm text-white font-medium">Font</Label>
                    <Select value={fontFamily} onValueChange={setFontFamily}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        {FONTS.map(f => <SelectItem key={f} value={f} className="text-white">{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-white font-medium">Size</Label>
                    <Input
                      type="number"
                      value={fontSize}
                      onChange={(e) => setFontSize(parseInt(e.target.value))}
                      className="bg-zinc-800 border-zinc-600 text-white mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-sm text-white font-medium">Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        type="color" 
                        value={textColor} 
                        onChange={(e) => setTextColor(e.target.value)} 
                        className="w-12 h-10 p-1 bg-zinc-800 border-zinc-600 cursor-pointer" 
                      />
                      <Input 
                        value={textColor} 
                        onChange={(e) => setTextColor(e.target.value)}
                        className="flex-1 bg-zinc-800 border-zinc-600 text-white text-xs"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-white font-medium">Stroke</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        type="color" 
                        value={strokeColor} 
                        onChange={(e) => setStrokeColor(e.target.value)} 
                        className="w-12 h-10 p-1 bg-zinc-800 border-zinc-600 cursor-pointer" 
                      />
                      <Input 
                        value={strokeColor} 
                        onChange={(e) => setStrokeColor(e.target.value)}
                        className="flex-1 bg-zinc-800 border-zinc-600 text-white text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-white font-medium">Stroke Width: {strokeWidth}px</Label>
                  <Slider value={[strokeWidth]} onValueChange={([v]) => setStrokeWidth(v)} max={10} className="mt-2" />
                </div>

                <div className="flex gap-2">
                  <Button variant={isBold ? 'default' : 'outline'} size="sm" onClick={() => setIsBold(!isBold)} className="flex-1">
                    <Bold className="w-4 h-4 mr-1" /> Bold
                  </Button>
                  <Button variant={isItalic ? 'default' : 'outline'} size="sm" onClick={() => setIsItalic(!isItalic)} className="flex-1">
                    <Italic className="w-4 h-4 mr-1" /> Italic
                  </Button>
                </div>

                <Button onClick={addTextLayer} className="w-full bg-violet-600 hover:bg-violet-500 text-white font-medium">
                  <Plus className="w-4 h-4 mr-2" /> Add Text
                </Button>
              </TabsContent>

              <TabsContent value="cta" className="space-y-2">
                <p className="text-sm text-white mb-3">Click to add CTA button:</p>
                <div className="grid grid-cols-2 gap-2">
                  {CTA_TEMPLATES.map((cta, i) => (
                    <Button
                      key={i}
                      onClick={() => addCTA(cta)}
                      style={{ backgroundColor: cta.bg, color: cta.color }}
                      className="h-12 font-bold text-sm hover:opacity-90 transition-opacity"
                    >
                      {cta.text}
                    </Button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="filters" className="space-y-4">
                <div>
                  <Label className="text-sm text-white font-medium">Brightness: {brightness}%</Label>
                  <Slider value={[brightness]} onValueChange={([v]) => setBrightness(v)} min={0} max={200} className="mt-2" />
                </div>
                <div>
                  <Label className="text-sm text-white font-medium">Contrast: {contrast}%</Label>
                  <Slider value={[contrast]} onValueChange={([v]) => setContrast(v)} min={0} max={200} className="mt-2" />
                </div>
                <Button variant="outline" className="w-full text-white border-zinc-600" onClick={() => { setBrightness(100); setContrast(100); }}>
                  Reset Filters
                </Button>
              </TabsContent>
            </Tabs>
          </Card>

          {selectedLayer && (
            <Card className="bg-zinc-900 border-zinc-700 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white font-medium">Selected Layer</span>
                <Button variant="ghost" size="sm" onClick={deleteSelectedLayer} className="text-red-400 hover:text-red-300 hover:bg-red-400/10">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
              <p className="text-xs text-zinc-400 mt-1">Click and drag on canvas to reposition</p>
            </Card>
          )}
        </div>

        {/* Canvas Area */}
        <div className="flex-1">
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <div className="relative">
              {/* Canvas is always rendered */}
              <canvas
                ref={canvasRef}
                width={800}
                height={500}
                onClick={handleCanvasClick}
                onMouseMove={handleCanvasDrag}
                className={`border border-zinc-800 rounded cursor-crosshair ${!image && textLayers.length === 0 ? 'opacity-0 absolute' : ''}`}
              />
              
              {/* Upload overlay when no image */}
              {!image && textLayers.length === 0 && (
                <div className="flex flex-col items-center justify-center h-[500px] border-2 border-dashed border-zinc-700 rounded-lg">
                  <ImageIcon className="w-16 h-16 text-zinc-600 mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Load an Image to Edit</h3>
                  <p className="text-sm text-zinc-400 mb-4">Upload from computer or select from library</p>
                  <div className="flex gap-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                      <span className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-zinc-600 bg-zinc-800 text-white hover:bg-zinc-700 transition-colors">
                        Upload Image
                      </span>
                    </label>
                    <Button onClick={loadLibraryImages} className="bg-violet-600 hover:bg-violet-500">
                      From Library
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Right Panel - Quick Actions */}
        <div className="w-64 space-y-4">
          <Card className="bg-zinc-900 border-zinc-700 p-4">
            <h3 className="text-sm font-medium text-white mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <span className="flex items-center w-full px-4 py-2 rounded-md border border-zinc-600 text-white hover:bg-zinc-800 transition-colors">
                  <ImageIcon className="w-4 h-4 mr-2" /> Upload New Image
                </span>
              </label>
              <Button variant="outline" className="w-full justify-start text-white border-zinc-600 hover:bg-zinc-800" onClick={loadLibraryImages}>
                <Sparkles className="w-4 h-4 mr-2" /> Load from Library
              </Button>
              <Button variant="outline" className="w-full justify-start text-white border-zinc-600 hover:bg-zinc-800" onClick={() => navigate('/images')}>
                <Plus className="w-4 h-4 mr-2" /> Generate New Image
              </Button>
            </div>
          </Card>

          {showLibrary && libraryImages.length > 0 && (
            <Card className="bg-zinc-900 border-zinc-800 p-4">
              <h3 className="text-sm font-medium mb-3">Library</h3>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {libraryImages.map((img) => (
                  <button
                    key={img.id}
                    onClick={() => {
                      const src = img.image_base64.startsWith('data:') ? img.image_base64 : `data:image/png;base64,${img.image_base64}`;
                      setImage(src);
                      setShowLibrary(false);
                    }}
                    className="aspect-square rounded overflow-hidden border-2 border-transparent hover:border-violet-500"
                  >
                    <img
                      src={img.image_base64.startsWith('data:') ? img.image_base64 : `data:image/png;base64,${img.image_base64}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
